import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * Recurring Transaction Types
 */
export type RecurringTransactionType = "invoice" | "bill" | "journal_entry";

/**
 * Frequency options for recurring transactions
 */
export type RecurringFrequency = "daily" | "weekly" | "monthly" | "yearly";

/**
 * End type for recurring transactions
 */
export type RecurringEndType = "never" | "on_date" | "after_occurrences";

/**
 * Recurring transaction status
 */
export type RecurringStatus = "active" | "paused" | "completed" | "cancelled";

/**
 * Instance status for generated transactions
 */
export type InstanceStatus = "pending" | "approved" | "posted" | "skipped";

/**
 * Schedule pattern for recurring transactions
 */
export interface RecurringSchedule {
  frequency: RecurringFrequency;
  intervalValue: number; // e.g., every 2 weeks
  dayOfWeek?: number; // 0-6 for weekly
  weekOfMonth?: number; // 1-5 for "first Tuesday", etc.
  dayOfMonth?: number; // 1-31 for monthly
  monthOfYear?: number; // 1-12 for yearly
}

/**
 * Recurring Transaction record
 * Per Phase 3: Recurring Transactions
 */
export interface RecurringTransaction {
  id: string;
  name: string;
  description?: string;
  type: RecurringTransactionType;
  companyId: string;
  companyName?: string;

  // Template data (JSON of the transaction to create)
  templateData: string;

  // Amount (can be 0 for variable amounts)
  amount: number;
  requiresApprovalIfZero: boolean;

  // Schedule
  schedule: RecurringSchedule;
  endType: RecurringEndType;
  endDate?: string;
  remainingOccurrences?: number;

  // Status
  status: RecurringStatus;
  nextOccurrence?: string;
  lastRun?: string;
  runCount: number;

  // Audit
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Recurring Transaction Instance (generated transaction)
 */
export interface RecurringTransactionInstance {
  id: string;
  recurringTransactionId: string;
  recurringTransactionName: string;
  type: RecurringTransactionType;
  scheduledDate: string;
  status: InstanceStatus;
  amount: number;
  generatedTransactionId?: string;
  approvedBy?: string;
  approvedAt?: string;
  skippedBy?: string;
  skippedAt?: string;
  skippedReason?: string;
}

/**
 * Form data for creating/editing recurring transactions
 */
export interface RecurringTransactionFormData {
  name: string;
  description: string;
  type: RecurringTransactionType;
  companyId: string;
  amount: number;
  requiresApprovalIfZero: boolean;
  frequency: RecurringFrequency;
  intervalValue: number;
  dayOfWeek?: number;
  weekOfMonth?: number;
  dayOfMonth?: number;
  monthOfYear?: number;
  endType: RecurringEndType;
  endDate: string;
  remainingOccurrences: number;
  templateData: string;
}

/**
 * RecurringTransactions Store
 *
 * Manages recurring transactions per Phase 3
 * - Create/edit recurring transaction templates
 * - Schedule configuration
 * - Pending instance approval
 * - "Make Recurring" from existing transactions
 */
class RecurringTransactionsStore {
  rootStore: RootStore;

  // Selection
  selectedId: string | null = null;
  selectedInstanceId: string | null = null;

  // Form state
  isFormOpen = false;
  isEditing = false;
  formData: RecurringTransactionFormData = this.getDefaultFormData();

  // UI state
  error: string | null = null;
  activeTab: "templates" | "pending" = "templates";

  // Filters
  typeFilter: RecurringTransactionType | "all" = "all";
  statusFilter: RecurringStatus | "all" = "all";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== ServiceStore Access ====================

  get recurringTransactionsServiceStore() {
    return this.rootStore.serviceStores?.["recurring-transactions"];
  }

  get recurringTransactionInstancesServiceStore() {
    return this.rootStore.serviceStores?.["recurring-transaction-instances"];
  }

  // ==================== Computed: Data ====================

  get recurringTransactions(): RecurringTransaction[] {
    return (this.recurringTransactionsServiceStore?.entities as RecurringTransaction[]) || [];
  }

  get pendingInstances(): RecurringTransactionInstance[] {
    return (this.recurringTransactionInstancesServiceStore?.entities as RecurringTransactionInstance[]) || [];
  }

  get isLoading(): boolean {
    return this.recurringTransactionsServiceStore?.isFindPending || false;
  }

  /**
   * Get default form data
   */
  private getDefaultFormData(): RecurringTransactionFormData {
    return {
      name: "",
      description: "",
      type: "invoice",
      companyId: "",
      amount: 0,
      requiresApprovalIfZero: true,
      frequency: "monthly",
      intervalValue: 1,
      dayOfMonth: 1,
      endType: "never",
      endDate: "",
      remainingOccurrences: 12,
      templateData: "{}",
    };
  }

  /**
   * Selected recurring transaction
   */
  get selectedRecurringTransaction(): RecurringTransaction | null {
    if (!this.selectedId) return null;
    return this.recurringTransactions.find((r) => r.id === this.selectedId) || null;
  }

  /**
   * Filtered recurring transactions
   */
  get filteredRecurringTransactions(): RecurringTransaction[] {
    return this.recurringTransactions.filter((rt) => {
      if (this.typeFilter !== "all" && rt.type !== this.typeFilter) return false;
      if (this.statusFilter !== "all" && rt.status !== this.statusFilter) return false;
      return true;
    });
  }

  /**
   * Filtered pending instances
   */
  get filteredPendingInstances(): RecurringTransactionInstance[] {
    return this.pendingInstances.filter((inst) => inst.status === "pending");
  }

  /**
   * Zero-amount pending instances that require approval with amount
   */
  get zeroAmountPendingInstances(): RecurringTransactionInstance[] {
    return this.pendingInstances.filter((inst) => {
      if (inst.status !== "pending") return false;
      if (inst.amount !== 0) return false;

      // Check if the recurring transaction requires approval for zero amounts
      const rt = this.recurringTransactions.find(
        (r) => r.id === inst.recurringTransactionId
      );
      return rt?.requiresApprovalIfZero === true;
    });
  }

  /**
   * Non-zero pending instances (can be auto-approved)
   */
  get nonZeroPendingInstances(): RecurringTransactionInstance[] {
    return this.pendingInstances.filter((inst) => {
      if (inst.status !== "pending") return false;
      return inst.amount > 0;
    });
  }

  /**
   * Count of pending instances requiring attention
   */
  get pendingApprovalCount(): number {
    return this.filteredPendingInstances.length;
  }

  /**
   * Count of zero-amount instances requiring amount entry
   */
  get zeroAmountPendingCount(): number {
    return this.zeroAmountPendingInstances.length;
  }

  /**
   * Load recurring transactions
   */
  async loadRecurringTransactions(): Promise<void> {
    this.error = null;
    try {
      await this.recurringTransactionsServiceStore?.find({ query: { $limit: 200 } });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to load recurring transactions";
      });
    }
  }

  /**
   * Load pending instances
   */
  async loadPendingInstances(): Promise<void> {
    try {
      await this.recurringTransactionInstancesServiceStore?.find({
        query: { status: "pending", $limit: 200 },
      });
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * Create recurring transaction
   */
  async createRecurringTransaction(): Promise<void> {
    this.error = null;

    try {
      await this.recurringTransactionsServiceStore?.create({
        name: this.formData.name,
        description: this.formData.description,
        type: this.formData.type,
        companyId: this.formData.companyId,
        templateData: this.formData.templateData,
        amount: this.formData.amount,
        requiresApprovalIfZero: this.formData.requiresApprovalIfZero,
        schedule: {
          frequency: this.formData.frequency,
          intervalValue: this.formData.intervalValue,
          dayOfWeek: this.formData.dayOfWeek,
          weekOfMonth: this.formData.weekOfMonth,
          dayOfMonth: this.formData.dayOfMonth,
          monthOfYear: this.formData.monthOfYear,
        },
        endType: this.formData.endType,
        endDate: this.formData.endDate,
        remainingOccurrences: this.formData.remainingOccurrences,
        status: "active",
      });
      this.closeForm();
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to create recurring transaction";
      });
    }
  }

  /**
   * Update recurring transaction
   */
  async updateRecurringTransaction(): Promise<void> {
    if (!this.selectedId) return;

    this.error = null;

    try {
      await this.recurringTransactionsServiceStore?.patch(this.selectedId, {
        name: this.formData.name,
        description: this.formData.description,
        amount: this.formData.amount,
        requiresApprovalIfZero: this.formData.requiresApprovalIfZero,
        schedule: {
          frequency: this.formData.frequency,
          intervalValue: this.formData.intervalValue,
          dayOfWeek: this.formData.dayOfWeek,
          weekOfMonth: this.formData.weekOfMonth,
          dayOfMonth: this.formData.dayOfMonth,
          monthOfYear: this.formData.monthOfYear,
        },
        endType: this.formData.endType,
        endDate: this.formData.endDate,
        remainingOccurrences: this.formData.remainingOccurrences,
        templateData: this.formData.templateData,
      });
      this.closeForm();
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to update recurring transaction";
      });
    }
  }

  /**
   * Pause recurring transaction
   */
  async pauseRecurringTransaction(id: string): Promise<void> {
    try {
      await this.recurringTransactionsServiceStore?.patch(id, { status: "paused" });
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * Resume recurring transaction
   */
  async resumeRecurringTransaction(id: string): Promise<void> {
    try {
      await this.recurringTransactionsServiceStore?.patch(id, { status: "active" });
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * Cancel recurring transaction
   */
  async cancelRecurringTransaction(id: string): Promise<void> {
    try {
      await this.recurringTransactionsServiceStore?.patch(id, { status: "cancelled" });
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * Approve pending instance
   */
  async approveInstance(instanceId: string): Promise<void> {
    const instance = this.pendingInstances.find((i) => i.id === instanceId);
    if (!instance) return;

    // Check if this is a zero-amount instance that requires amount entry
    if (instance.amount === 0) {
      const rt = this.recurringTransactions.find(
        (r) => r.id === instance.recurringTransactionId
      );
      if (rt?.requiresApprovalIfZero) {
        throw new Error("This instance requires an amount to be entered before approval.");
      }
    }

    await this.recurringTransactionInstancesServiceStore?.patch(instanceId, {
      status: "approved",
    });
  }

  /**
   * Approve pending instance with a specific amount
   * Used for zero-amount recurring transactions that require manual amount entry
   */
  async approveInstanceWithAmount(instanceId: string, amount: number): Promise<void> {
    if (amount <= 0) {
      throw new Error("Amount must be greater than zero.");
    }

    await this.recurringTransactionInstancesServiceStore?.patch(instanceId, {
      amount,
      status: "approved",
    });
  }

  /**
   * Skip pending instance
   */
  async skipInstance(instanceId: string, reason: string): Promise<void> {
    try {
      await this.recurringTransactionInstancesServiceStore?.patch(instanceId, {
        status: "skipped",
        skippedReason: reason,
      });
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * Make recurring from existing transaction
   */
  async makeRecurring(transactionType: RecurringTransactionType, transactionId: string): Promise<void> {
    this.formData.type = transactionType;
    this.formData.templateData = JSON.stringify({ sourceId: transactionId });
    this.openCreateForm();
  }

  /**
   * Select recurring transaction
   */
  selectRecurringTransaction(id: string | null): void {
    this.selectedId = id;
  }

  /**
   * Set active tab
   */
  setActiveTab(tab: "templates" | "pending"): void {
    this.activeTab = tab;
  }

  /**
   * Set type filter
   */
  setTypeFilter(filter: RecurringTransactionType | "all"): void {
    this.typeFilter = filter;
  }

  /**
   * Set status filter
   */
  setStatusFilter(filter: RecurringStatus | "all"): void {
    this.statusFilter = filter;
  }

  /**
   * Open create form
   */
  openCreateForm(): void {
    this.formData = this.getDefaultFormData();
    this.isEditing = false;
    this.isFormOpen = true;
  }

  /**
   * Open edit form
   */
  openEditForm(id: string): void {
    const transaction = this.recurringTransactions.find((r) => r.id === id);
    if (!transaction) return;

    this.selectedId = id;
    this.formData = {
      name: transaction.name,
      description: transaction.description || "",
      type: transaction.type,
      companyId: transaction.companyId,
      amount: transaction.amount,
      requiresApprovalIfZero: transaction.requiresApprovalIfZero,
      frequency: transaction.schedule.frequency,
      intervalValue: transaction.schedule.intervalValue,
      dayOfWeek: transaction.schedule.dayOfWeek,
      weekOfMonth: transaction.schedule.weekOfMonth,
      dayOfMonth: transaction.schedule.dayOfMonth,
      monthOfYear: transaction.schedule.monthOfYear,
      endType: transaction.endType,
      endDate: transaction.endDate || "",
      remainingOccurrences: transaction.remainingOccurrences || 12,
      templateData: transaction.templateData,
    };
    this.isEditing = true;
    this.isFormOpen = true;
  }

  /**
   * Close form
   */
  closeForm(): void {
    this.isFormOpen = false;
    this.isEditing = false;
    this.selectedId = null;
    this.formData = this.getDefaultFormData();
  }

  /**
   * Update form field
   */
  updateFormField<K extends keyof RecurringTransactionFormData>(
    field: K,
    value: RecurringTransactionFormData[K]
  ): void {
    this.formData[field] = value;
  }
}

export default RecurringTransactionsStore;
